import { leaguesV2Api } from "../leaguesV2Api";
import type {
  ClubhouseLeagueResponseDto,
  Clubhouse_PostLeagueV2,
} from "../../types/leagues";
import type { Clubhouse_LeagueV2FinalizeResultDto } from "../../types/leagueDetails";

export type CreateLeagueMutationArgs = Clubhouse_PostLeagueV2 & {
  facilityId: string;
};

type FinalizeLeagueArgs = {
  facilityId: string;
  leagueId: string;
};

type DeleteDraftLeagueArgs = {
  facilityId: string;
  leagueId: string;
};

export type UpdateLeagueMutationArgs = {
  facilityId: string;
  leagueId: string;
  body: Partial<Clubhouse_PostLeagueV2>;
};

export const updateLeagueQuery = ({
  facilityId,
  leagueId,
  body,
}: UpdateLeagueMutationArgs) => ({
  url: `/api/facility/${facilityId}/leagues/${leagueId}`,
  method: "PATCH" as const,
  body,
});

const leagueMutationEndpoints = leaguesV2Api.injectEndpoints({
  endpoints: (build) => ({
    createLeague: build.mutation<
      ClubhouseLeagueResponseDto,
      CreateLeagueMutationArgs
    >({
      query: (args: CreateLeagueMutationArgs) => ({
        url: `/api/facility/${args.facilityId}/leagues`,
        method: "POST",
        body: args,
      }),
      invalidatesTags: ["League", "Dashboard"],
    }),

    finalizeLeague: build.mutation<
      Clubhouse_LeagueV2FinalizeResultDto,
      FinalizeLeagueArgs
    >({
      query: ({ facilityId, leagueId }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/finalize`,
        method: "POST",
      }),
      invalidatesTags: (_, __, { leagueId }) => [
        { type: "League", id: leagueId },
        "Dashboard",
        // Backend auto-scores on finalize — refresh the standings read.
        { type: "Standings", id: leagueId },
      ],
    }),

    updateLeague: build.mutation<
      ClubhouseLeagueResponseDto,
      UpdateLeagueMutationArgs
    >({
      query: updateLeagueQuery,
      invalidatesTags: (_, __, { leagueId }) => [
        { type: "League", id: leagueId },
        "Dashboard",
      ],
    }),

    // Hard-deletes a league that is still a draft; the server answers 409 for
    // anything else. A 409 means the league was finalized while the operator
    // was looking at it, so the stale draft is refreshed away like a success.
    deleteDraftLeague: build.mutation<void, DeleteDraftLeagueArgs>({
      query: ({ facilityId, leagueId }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, error, { leagueId }) =>
        !error || error.status === 409
          ? [{ type: "League", id: leagueId }, "Dashboard"]
          : [],
    }),
  }),
});

export const {
  useCreateLeagueMutation,
  useFinalizeLeagueMutation,
  useUpdateLeagueMutation,
  useDeleteDraftLeagueMutation,
} = leagueMutationEndpoints;
